/**
 * nalli-cart-shipping.js — WS8 (Nalli rebuild).
 *
 * Single shared shipping-note module. Replaces BOTH live patches:
 *   - live component-cart-items.js  updateshipping()  (after qty change / line removal)
 *   - live product-form.js          shipping block inside animateAddToCart() (after ATC)
 *
 * Listens document-level for Horizon 4.1.3's cart update event
 * (`CartLinesUpdateEvent` from @shopify/events = 'shopify:cart:lines-update',
 * dispatched with bubbles by product-form.js, component-cart-items.js and
 * quick-order-list.js for add / update / remove). Waits for the event's
 * deferred `promise` (the actual cart mutation) to settle, then:
 *   1. fetches the custom JSON view `/cart?view=data` (templates/cart.data.liquid:
 *      fabric-adjusted count, weight in kg, subtotal, voucher/nonvoucher counts);
 *   2. derives the shipping tier label (live rules, verbatim; "V" suffix =
 *      subtotal >= international free-shipping threshold);
 *   3. persists it as cart attribute `cm-cart-notes` via /cart/update.js
 *      (consumed by the shipping-rate / fulfillment integration);
 *   4. resets the WS7 gift-voucher gate (#dataform) when the cart no longer
 *      mixes vouchers and products.
 *
 * Threshold comes from theme settings via the #nalli-shipping-config JSON
 * element emitted by snippets/nalli-head.liquid (default 39999).
 * Console/alert debris from the live patches stripped.
 * Exposed as window.NalliCart.updateShippingNote for WS7 reuse.
 */

const CART_LINES_UPDATE = 'shopify:cart:lines-update';
const ATTRIBUTE_NAME = 'cm-cart-notes';
const DEBOUNCE_MS = 300;

function readConfig() {
  const el = document.getElementById('nalli-shipping-config');
  if (!el) return {};
  try {
    return JSON.parse(el.textContent) || {};
  } catch {
    return {};
  }
}

/**
 * Live tier rules, ported verbatim from the patched component-cart-items.js /
 * product-form.js (identical blocks on live — deduped here).
 * @param {number} totalCount fabric-adjusted item count (cart.data "count")
 * @param {number} totalWeight cart weight in kg (cart.data "weight")
 * @param {number} totalPrice subtotal in whole currency units (cart.data "subtotal")
 * @param {number} intlThreshold free-shipping threshold for the "V" tier
 * @returns {string} shipping note ('' when no rule matches)
 */
export function computeShippingNote(totalCount, totalWeight, totalPrice, intlThreshold) {
  let noteship = '';

  if (totalPrice < intlThreshold) {
    // ---------- RULES FOR SUBTOTAL < threshold ----------
    if (totalCount <= 3) {
      noteship = totalWeight <= 3 ? 'Standard up to 3 kg' : 'Standard - 3 products more than 3kg';
    } else if (totalCount >= 4 && totalCount <= 50) {
      noteship = `Standard ${totalCount} Product`;
    }
  } else {
    // ---------- RULES FOR SUBTOTAL >= threshold ----------
    if (totalCount <= 3) {
      noteship = totalWeight <= 3 ? 'Standard up to 3 kg V' : 'Standard - 3 products more than 3kg V';
    } else if (totalCount >= 4 && totalCount <= 51) {
      // Ranges of 3: 4-6, 7-9, 10-12, ...
      const start = Math.floor((totalCount - 1) / 3) * 3 + 1;
      const end = start + 2;
      noteship = `Standard ${start}-${end} products V`;
    }
  }

  return noteship;
}

/**
 * Re-enables the WS7 voucher-gated product form when the cart no longer
 * contains a voucher/product mix (counterpart of the gate in
 * nalli-voucher-gate.js — see rebuild/specs/assets--product-form.js.md).
 * @param {{voucher?: number, nonvoucher?: number}} data
 */
function resetVoucherGate(data) {
  const dataformEl = document.getElementById('dataform');
  if (!dataformEl) return;
  if (data.voucher === 0 && data.nonvoucher === 0) {
    dataformEl.textContent = '1';

    const productForm = document.querySelector('product-form-component');
    productForm?.classList.remove('disabled');

    const errorIcon = document.querySelector('.product-form-text__error');
    errorIcon?.classList.add('hidden');
  }
}

/**
 * Fetches /cart?view=data, recomputes the shipping tier and writes the
 * `cm-cart-notes` cart attribute. Safe to call from anywhere (WS7 reuses it).
 * @returns {Promise<string>} the note that was written ('' if none)
 */
export async function updateShippingNote() {
  const config = readConfig();
  const intlThreshold = Number(config.intl_threshold) || 39999;

  let data;
  try {
    const response = await fetch('/cart?view=data');
    data = await response.json();
  } catch {
    return '';
  }

  resetVoucherGate(data);

  const noteship = computeShippingNote(
    Number(data.count) || 0,
    Number(data.weight) || 0,
    Number(data.subtotal) || 0,
    intlThreshold
  );

  if (!noteship) return '';

  try {
    await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { [ATTRIBUTE_NAME]: noteship } }),
    });
  } catch {
    return '';
  }

  return noteship;
}

let debounceTimer = null;

function scheduleUpdate() {
  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => {
    debounceTimer = null;
    updateShippingNote();
  }, DEBOUNCE_MS);
}

document.addEventListener(CART_LINES_UPDATE, (event) => {
  // 4.1.3 attaches the in-flight cart mutation as event.promise — wait for it
  // to settle so /cart?view=data reflects the new cart (replaces the live
  // setTimeout(…, 1000) hack). Fall through immediately when absent.
  const promise = /** @type {any} */ (event).promise;
  const settled = promise && typeof promise.then === 'function' ? promise.catch(() => {}) : Promise.resolve();
  settled.then(scheduleUpdate);
});

window.NalliCart = Object.assign(window.NalliCart || {}, { updateShippingNote, computeShippingNote });
